/**
  * @class meleTabs
  * @description
  *
  * # Purpose
  * Implements `<mele-tabs>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-tabs></mele-tabs>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-tabs',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleContainer from 'meleContainer'

const BUTTON_TEMPLATES = {
  prev: { key: 'prev', color: 'gray', label: '<<', action: '' },
  counter: { key: 'counter', type: 'counter' },
  next: { key: 'next', color: 'gray', label: '>>', action: '' },
}

const PROPERTIES = _.extend({}, {
  selected: { type: 'string', description: 'selected tab' },
  phase: { type: 'string', readonly: 'true', description: 'indicates position of selected tab within all tabs', enum: ['none', 'first', 'first last', 'middle', 'last'] },
  width: { type: 'string', description: "Sets the width of the element in the window, regardles of it's type. Options for width: 1, 1/2, 1/3, 1/4" },
  footerbuttons: { type: 'array', description: 'definition of header buttons; array of buttons with either button JS definition or shortcuts "new","clear","undo","submit"' },
  direction: { type: 'string', description: 'tab buttons direction', enum: ['horizontal', 'vertical', 'none'], default: 'horizontal' },
  mode: { type: 'string', description: 'sets behaviour of tabs', enum: ['standard', 'listlike', 'scrollable'], default: 'standard' },
}, meleContainer._properties)

export default class meleTabs extends meleContainer {
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
*/
  init() {
    super.init()

    this._wrapper = this.prependChildren({ tag: 'mele-wrapper' })
    this._tabs_wrapper = this._wrapper.prependChildren({ tag: 'mele-tabs-wrapper' })
    this._tabs = this._tabs_wrapper.appendChildren({ tag: 'mele-list' })
    this._tabs.eventSelected = (self, items, old, context_user_interaction)=>{
      if(context_user_interaction) this.setProperty('selected', items[0], true)
    }

    this._body_wrapper = this._wrapper.appendChildren({ tag: 'mele-body-wrapper' })
    this._body = this._body_wrapper.appendChildren({ tag: 'mele-body' })
    this._footer_wrapper = this._wrapper.appendChildren({ tag: 'mele-footer-wrapper' })
    this._footer = this._footer_wrapper.prependChildren({ tag: 'mele-footer', key: 'core_footer', class: 'empty' })
    this._footer_texts = this._footer.appendChildren({ tag: 'mele-footer-texts' })

    // move all other already existing subelements into body
    while(this.childNodes.length>1) {
      const node = this.childNodes[1] // take second child and move it where appropriate
      const node_name = _.toLower(node.nodeName)
      if(node_name==='mele-footer') {
        // move all <mele-footer> tag under mele-wrapper/mele-footer-wrapper
        this._footer_wrapper.appendChild(node)
      }else{
        // move all other children under mele-wrapper/mele-body-wrapper/mele-body
        this._body.appendChild(node)
      }
    }

    // setup MutationObserver to listen to DOM changes in mele-body to trigger refresh of tabs
    this._observer = new MutationObserver(()=>this.refreshTabs())
    this._observer.observe(this._body, { attributes: false, childList: true, characterData: false })

    setTimeout(()=>{
      this.setProperty('direction', PROPERTIES.direction?.default, undefined, true, /*skip_update_time*/ true)
      this.refreshTabs()
    }, 100)
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * When element is removed from DOM, stop MutationObserver
  */
  disconnectedCallback() {
    if(this._observer) {
      this._observer.disconnect()
      delete this._observer
    }
  } // disconnectedCallback

  directionChanged(value) {
    if(value) {
      this._tabs.direction = value === 'horizontal' ? 'row': 'column'
    }
  }

  modeChanged(value) {
    if(value==='listlike') {
      this._body_wrapper.appendChildren({
        tag: 'mele-button',
        type: 'item',
        class: 'tab-detail-back',
        iconleft: 'direction.w',
        label: 'tabs.back_to_records',
        eventButtonClicked: ()=>{
          this.selected = ''
        },
      })
    }
  }

  getAttr(el, attr) {
    return String((el.getAttribute ? el.getAttribute(attr) : null) ?? el?.[attr] ?? '')
  } // getAttr

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * recalculate list with tabs
  */
  refreshTabs() {
    const selected = String(this.selected ?? '')

    // identify tabs from children of mele-body
    // this is useful when child used as tab source does not have defined label/label2/... as custom webcomponent attributes
    const tabs = this._body.findAll('>[key]:not([disabled])')
      .filter(el=>this.ownerDocument.defaultView.getComputedStyle(el).visibility !== 'hidden')
      .map(el=>({
        key: this.getAttr(el, 'key'),
        label: this.getAttr(el, 'label'),
        label2: this.getAttr(el, 'label2'),
        iconleft: this.getAttr(el, 'icon'),
        // value: this.getAttr(el, 'count') || this.getAttr(el, 'value'),
        value: this.getAttr(el, 'count'),
      }))

    const old_tabs = (this._tabs.items || [])
      .map(el=>({
        key: this.getAttr(el, 'key'),
        label: this.getAttr(el, 'label'),
        label2: this.getAttr(el, 'label2'),
        iconleft: this.getAttr(el, 'icon'),
        value: this.getAttr(el, 'value'),
      }))

    // update tabs list if needed
    if(!_.isEqual(old_tabs, tabs)) this._tabs.items = tabs

    // adjust selected tab
    if(tabs.find(tab=>tab.key===selected)) {
      // selected tab exists
      this._tabs.selected = selected
    }else if(tabs.length>0) {
      this.selected = tabs[0].key
    }

    // adjust visibility of non-selected tab contents
    if(this.mode === 'scrollable') {
      this._body.forAll('>[key]:not([disabled])', el=>{
        el.hidden = false
        if(el.collapsible && el.getAttribute('key')===this._tabs.selected) {
          el.collapsed = false
        }
      })
    } else {
      this._body.forAll('>[key]:not([disabled])', el=>{
        const key = this.getAttr(el, 'key')
        const selected = String(this._tabs.selected??'')
        el.hidden = key !== selected
      })
    }

    // refresh phase attribute
    this.refreshPhase()
  } // refreshTabs

  refreshPhase() {
    const selected = String(this.selected??'')
    const tabs = this._tabs.items
    const count = tabs?.length || 0
    let idx = tabs?.findIndex(tab=>String(tab.key??'')===selected)
    if(!(idx>0)) idx = 0
    const phase =
      count === 0 ? 'none' :
        count === 1 ? 'first last' :
          idx === 0 ? 'first' :
            idx === count-1 ? 'last' :
              'middle'

    this.phase = phase

    this._footer.forAll('[shift]', el=>{
      const shift = String(el.getAttribute('shift'))
      const step = parseInt(shift)
      el.disabled =
        phase === 'none' ||
        (shift === 'first' && idx === 0) ||
        (shift === 'last' && idx === count-1) ||
        (step < 0 && idx === 0) ||
        (step > 0 && idx === count-1)
    })
  } // refreshPhase

  selectedIdx() {
    const selected = String(this.selected??'')
    const tabs = this._tabs.items
    const count = tabs?.length || 0
    if(count === 0) return -1
    let idx = tabs?.findIndex(tab=>String(tab.key??'')===selected)
    if(!(idx>0)) idx = 0
    return idx
  } // selectedIdx

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "selected"
  */
  selectedChanged(value, old, user_input) {
    const selected = String(value ?? '')
    this._tabs.selected = selected
    if(this.mode === 'scrollable') {
      this._body.forAll('>[key]', el=>{
        el.hidden = false
      })
    } else {
      const items = this._body.findAll('>[key]:not([disabled])')
      if(items.length === 0) return
      const keys = items.map(el=>this.getAttr(el, 'key'))

      if(!keys.includes(selected)) {
        this._tabs.selected = keys[0]
      }

      for(const item of items) {
        const key = this.getAttr(item, 'key')
        item.hidden = key !== String(selected??'')
      }
    }

    this.refreshPhase()

    this.eventSelected(this, selected, old, user_input)
  } // selectedChanged

  eventSelected(self, items, old, context_user_interaction) {
    // if(context_user_interaction)
    this.publish('mmTabSelected', { holder: this.key, selected: items, old, interactive: !!context_user_interaction })
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "footerbuttons"
  */
  footerbuttonsChanged() {
    this._footer.forAll('>*', el=>{
      if(_.toLower(el.tagName)!=='mele-footer-texts') { el.deleteElement() }
    })
    this.footerbuttons.forEach(button=>{
      if(typeof (button)==='string') { button = BUTTON_TEMPLATES[button] }
      if(_.isObjectLike(button)) {
        button = _.extend({ tag: 'mele-button', type: 'actionbar' }, button)
        this._footer.prependChildren(button, this._footer_texts)
      }
    })
    this._setFooterEmptyFlag()
  } // footerbuttonsChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * set "empty" class if there is only 1 child (i.e. mele-footer-texts) and all of its children are empty
  */
  _setFooterEmptyFlag() {
    this._footer.classList.toggle(
      'empty',
      this._footer.childElementCount===1 && !this._footer.find('>mele-footer-texts >mele-footer-text:not(:empty)'),
    )
    this._footer_texts.classList.toggle(
      'empty',
      !this._footer_texts.find('>mele-footer-text:not(:empty)'),
    )
  } // _setFooterEmptyFlag

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "footerbuttons"
  */
  selectFirst() {
    if(!this._body) return
    const elFirst = this.getFirstItemElement()
    if(elFirst) { this.selected = elFirst.key }
  } // selectFirst

  formPrev() {
    const selected = String(this.selected??'')
    const tabs = this._tabs.items
    const count = tabs?.length || 0
    if(count === 0) return
    let idx = tabs?.findIndex(tab=>String(tab.key??'')===selected)
    if(!(idx>0)) idx = 0
    idx--
    if(idx < 0) idx = 0
    if(idx >= count) idx = count-1
    this.selected = tabs[idx].key
  } // formPrev

  formNext() {
    const selected = String(this.selected??'')
    const tabs = this._tabs.items
    const count = tabs?.length || 0
    if(count === 0) return
    let idx = tabs?.findIndex(tab=>String(tab.key??'')===selected)
    if(!(idx>0)) idx = 0
    idx++
    if(idx < 0) idx = 0
    if(idx >= count) idx = count-1
    this.selected = tabs[idx].key
  } // formNext
} // class meleTabs

meleTabs._properties = PROPERTIES
meleTabs.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-tabs', meleTabs)
